import { ChangeEvent, FocusEvent, useState } from 'react';

export interface TextInputAttrs {
  value: string;
  onChange(e: ChangeEvent<HTMLInputElement>): void;
  onBlur(e: FocusEvent<HTMLInputElement>): void;
}

export interface UseTextInput<T> {
  value: T;
  update(v: T): void;
  inputAttrs: TextInputAttrs;
}

export function useTextInput<T>(
  initialValue: T,
  setter: (v: string) => T,
  formatter: (v: T) => string = (v) => String(v)
): UseTextInput<T> {
  const [value, setValue] = useState(initialValue);
  const [formatted, setFormatted] = useState(formatter(initialValue));

  /**
   * Update the value directly (eg. reset value)
   *
   * @param newValue The new value
   */
  function update(newValue: T) {
    setValue(newValue);
    setFormatted(formatter(newValue));
  }

  /**
   * Update the real value as well as the input's text value
   * Copy the exact typed value to prevent text jumping/changing while typing
   *
   * @param e The input ChangeEvent
   */
  function onChange(e: ChangeEvent<HTMLInputElement>) {
    setValue(setter(e.currentTarget.value));
    setFormatted(e.currentTarget.value);
  }

  /**
   * When the user moves on from this input, format the real value into the input
   *
   * @param e The input blur FocusEvent
   */
  function onBlur(e: FocusEvent<HTMLInputElement>) {
    setFormatted(formatter(value));
  }

  return {
    value,
    update,
    inputAttrs: { value: formatted, onChange, onBlur },
  };
}
